import React, { useState } from 'react';
import { ActivityIcon, BrainIcon, FolderIcon, GlobeIcon, PlugZapIcon, SparklesIcon, TableIcon, WorkflowIcon } from 'lucide-react';
import { SectionIntro } from './SectionIntro';
import { brandMark } from './AppChrome';
const ORANGE = '#EA580C';
const INK = '#2B211A';
/**
 * The lift on Giga's own three blocks. A top inner highlight plus a deep,
 * wide cast — a flat drop shadow at 24px read as a sticker on paper, where a
 * lit edge and a longer falloff read as a solid object above the surface.
 * This is what tells the core blocks from the peripherals now that they no
 * longer differ in shape.
 */
const CORE_SHADOW = 'inset 0 1px 0 rgba(255,255,255,0.09), 0 2px 4px rgba(43,33,26,0.10), 0 14px 34px -14px rgba(43,33,26,0.45)';
const FADE = '#C9BDAE';
const BORDER = '#EAE4D9';
type NodeId = 'fs' | 'apps' | 'mem' | 'skills' | 'flows' | 'pulses' | 'tables' | 'cloudb' | 'agent' | 'mcp' | 'web' | 'slack' | 'teams' | 'gpt' | 'claude' | 'grok';
type GroupId = 'fp' | 'os' | 'pc';
type Node = {
  id: NodeId;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  sub?: string;
  group?: GroupId;
  /** Lucide icon for platform capabilities. */
  Icon?: React.ComponentType<{
    size?: number;
    strokeWidth?: number;
    color?: string;
  }>;
  /** Brand logo URL for third-party surfaces. */
  logo?: string;
};
const brand = (slug: string) => `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
const NODES: Node[] = [{
  id: 'fs',
  label: 'File System',
  x: 870,
  y: 606,
  w: 270,
  h: 44,
  Icon: FolderIcon
}, {
  id: 'apps',
  label: 'Connected Apps',
  x: 870,
  y: 60,
  w: 270,
  h: 84,
  sub: "Manage your team's connections to MCPs, APIs, and databases in one place",
  Icon: PlugZapIcon
}, {
  id: 'mem',
  label: 'Memory',
  x: 870,
  y: 172,
  w: 270,
  h: 84,
  sub: 'A company brain that answers in real time',
  Icon: BrainIcon
}, {
  id: 'skills',
  label: 'Skills',
  x: 870,
  y: 336,
  w: 270,
  h: 44,
  group: 'pc',
  Icon: SparklesIcon
}, {
  id: 'flows',
  label: 'Workflows',
  x: 870,
  y: 390,
  w: 270,
  h: 44,
  group: 'pc',
  Icon: WorkflowIcon
}, {
  id: 'pulses',
  label: 'Pulses',
  x: 870,
  y: 444,
  w: 270,
  h: 44,
  group: 'pc',
  Icon: ActivityIcon
}, {
  id: 'tables',
  label: 'Tables',
  x: 870,
  y: 498,
  w: 270,
  h: 44,
  group: 'pc',
  Icon: TableIcon
}, {
  id: 'cloudb',
  label: 'Cloud Browser',
  x: 870,
  y: 552,
  w: 270,
  h: 44,
  group: 'pc',
  Icon: GlobeIcon
}, {
  id: 'agent',
  label: 'Giga Runtime',
  // TALLER BLOCKS, SAME COPY. The description wraps to two lines at this
  // width, and at 74–80px tall that pair of lines was pressed hard against the
  // top and bottom edges with no padding left to give. 96px carries the title
  // and both lines with even clearance above and below.
  // The y values move with the height so each block keeps its centre, which is
  // what the diagonal tracks are drawn to.
  x: 430,
  y: 313,
  w: 300,
  h: 96,
  sub: 'A fully featured harness with its own file system'
}, {
  id: 'mcp',
  label: 'Giga MCP Server',
  x: 430,
  y: 161,
  w: 300,
  h: 96,
  sub: "Giga's capabilities, available to any MCP-compatible tool"
}, {
  // Giga's own mark, resolved through the same chain every other logo on the
  // page uses. It was falling through to the generic dot, so the one surface
  // that IS Giga was the only one without a mark.
  id: 'web',
  label: 'Giga Web Chat',
  x: 20,
  y: 414,
  w: 290,
  h: 56,
  group: 'fp',
  logo: brandMark('giga')
}, {
  id: 'slack',
  label: 'Giga in Slack',
  x: 20,
  y: 492,
  w: 290,
  h: 56,
  group: 'fp',
  logo: brand('slack')
}, {
  id: 'teams',
  label: 'Giga in Teams',
  x: 20,
  y: 570,
  w: 290,
  h: 56,
  group: 'fp',
  logo: brand('microsoft-teams')
}, {
  id: 'gpt',
  label: 'ChatGPT Work',
  x: 20,
  y: 110,
  w: 290,
  h: 56,
  group: 'os',
  logo: brand('chatgpt')
}, {
  id: 'claude',
  label: 'Claude Cowork',
  x: 20,
  y: 188,
  w: 290,
  h: 56,
  group: 'os',
  logo: brand('claude-ai')
}, {
  id: 'grok',
  label: 'Grokbot',
  x: 20,
  y: 266,
  w: 290,
  h: 56,
  group: 'os',
  logo: brand('grok')
}];
const GROUPS: {
  id: GroupId;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
}[] = [{
  // Left column and middle column shifted up 28px so all THREE columns share
  // one vertical centre. The right column reaches higher than the other two
  // (it starts at Connected Apps), so with everything on its original y the
  // left and middle sat low against it and the drawing read lopsided.
  id: 'fp',
  label: 'Giga surfaces',
  x: 4,
  y: 392,
  w: 322,
  h: 250
}, {
  id: 'os',
  label: 'Your existing surfaces',
  x: 4,
  y: 88,
  w: 322,
  h: 250
}, {
  id: 'pc',
  label: 'Platform capabilities',
  x: 854,
  y: 316,
  w: 302,
  h: 354
}];
const EDGES: {
  a: string;
  b: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  straight?: boolean;
  arrow?: boolean;
}[] = [{
  a: 'fs',
  b: 'agent',
  x1: 730,
  y1: 372,
  x2: 870,
  y2: 628
}, {
  a: 'apps',
  b: 'mcp',
  x1: 730,
  y1: 196,
  x2: 870,
  y2: 102
}, {
  a: 'mem',
  b: 'mcp',
  x1: 730,
  y1: 212,
  x2: 870,
  y2: 214
}, {
  a: 'pc',
  b: 'mcp',
  x1: 730,
  y1: 224,
  x2: 854,
  y2: 470
}, {
  a: 'agent',
  b: 'mcp',
  x1: 580,
  y1: 324,
  x2: 580,
  y2: 246,
  straight: true,
  arrow: true
}, {
  a: 'model',
  b: 'agent',
  x1: 580,
  y1: 476,
  x2: 580,
  y2: 398,
  straight: true
}, {
  a: 'agent',
  b: 'fp',
  x1: 326,
  y1: 517,
  x2: 430,
  y2: 372
}, {
  a: 'mcp',
  b: 'os',
  x1: 326,
  y1: 213,
  x2: 430,
  y2: 204
}];
// The copy-resolution machinery (LABELS, chainable combinations, fallbacks)
// is gone with it: it existed to describe arbitrary states the reader could
// reach by clicking blocks. The tabs are now the only control, so each
// configuration's copy lives on its own preset.
const MCP_ONLY_OFF: NodeId[] = ['agent', 'fs', 'web', 'slack', 'teams'];
const ALL_IN_OFF: NodeId[] = ['gpt', 'claude', 'grok'];
type WidgetState = {
  inactive: NodeId[];
  modelMode: 'giga' | 'byo';
};
// Exclusive presets: each names one whole configuration rather than a switch,
// so selecting one replaces the state instead of layering onto it. These are
// the ONLY control: the diagram below is a readout, so every state the reader
// can reach is one the label beneath the tabs actually describes.
const PRESETS: {
  id: string;
  label: string;
  /** What this configuration BUYS you, stated as a claim. Lives on the preset
   *  rather than being derived from the off-set: the tab is the only control,
   *  so each tab owns one line — and two tabs were resolving to the same
   *  sentence because they differ only by model mode. */
  headline: string;
  state: WidgetState;
}[] = [{
  id: 'alongside',
  label: 'Giga plus your existing tools',
  headline: 'Everything Giga does, alongside everything you already use.',
  state: {
    inactive: [],
    modelMode: 'giga'
  }
}, {
  id: 'mcp',
  label: 'Giga via MCP',
  headline: "All of Giga's power, inside the tools you already have.",
  state: {
    inactive: MCP_ONLY_OFF,
    modelMode: 'giga'
  }
}, {
  id: 'allin',
  label: 'All in on Giga',
  headline: 'Eliminate your Anthropic and OpenAI bills.',
  state: {
    inactive: ALL_IN_OFF,
    modelMode: 'giga'
  }
}, {
  id: 'byo',
  label: 'BYO model',
  headline: 'Bring your own model. Keep everything else.',
  state: {
    inactive: [],
    modelMode: 'byo'
  }
}];
const sameSet = (a: NodeId[], b: NodeId[]) => a.length === b.length && a.slice().sort().join(',') === b.slice().sort().join(',');
const nodeById = (id: NodeId) => NODES.find(n => n.id === id)!;
/** One surface or capability, stated as in or out of the configuration. */
function ReadoutRow({
  id,
  on,
  lit,
  veiled
}: {
  id: NodeId;
  on: boolean;
  lit?: boolean;
  veiled?: boolean;
}) {
  const n = nodeById(id);
  const Icon = n.Icon;
  return <li style={{
    opacity: veiled ? 0.32 : 1,
    // Set inline rather than as a second shadow utility: two arbitrary
    // shadow classes on one element resolve by stylesheet order, not by the
    // order they are written in the className.
    boxShadow: [on ? '0 1px 3px rgba(43,33,26,0.07)' : '', lit ? '0 0 0 3px rgba(234,88,12,0.2)' : ''].filter(Boolean).join(', ') || undefined
  }} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-[background-color,border-color,opacity,box-shadow] duration-300 ease-out ${on ? 'border-[#EAE4D9] bg-white' : 'border-dashed border-[#E4DCCF] bg-transparent'}`} data-id="element-541" data-mp="19kz4qwim6ycb">
      {Icon ? <Icon size={17} strokeWidth={1.9} color={on ? ORANGE : '#CFC3B2'} data-id="element-542" data-mp="4l2i6icvfk0v" /> : n.logo ? <img src={n.logo} alt="" aria-hidden="true" className="size-[18px] shrink-0 object-contain" style={{
      filter: on ? 'none' : 'grayscale(1) opacity(0.4)'
    }} data-id="element-543" data-mp="3mucsw2b94cm4" /> : null}
      <span className={`min-w-0 flex-1 truncate text-[14px] font-semibold ${on ? 'text-[#2B211A]' : 'text-[#B7AA9B]'}`} data-id="element-544" data-mp="1l2gjay8n9ctz">
        {n.label}
      </span>
      {/* The skipped items SAY they are skipped. On the desktop drawing a
       greyed block beside its lit neighbours is unambiguous; alone in a
       stacked list, "slightly paler" is not a state anyone can read. */}
      {!on && <span className="shrink-0 text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#C0B3A3]" data-id="element-545" data-mp="3jb482mjk65hu">
          Skipped
        </span>}
    </li>;
}
function ReadoutGroup({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-546" data-mp="3tb4uwigmz3qs">
      <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#A0917F]" data-id="element-547" data-mp="3tav3cqfy0oiz">
        {label}
      </p>
      <ul className="mt-2.5 space-y-1.5" data-id="element-548" data-mp="64cbn985hxy2">{children}</ul>
    </div>;
}
/** The core blocks keep their solid-ink treatment — weight is what tells
 *  Giga's own runtime from the things around it, on any screen. */
function CoreBlock({
  title,
  sub,
  on,
  lit,
  veiled
}: {
  title: string;
  sub: string;
  on: boolean;
  lit?: boolean;
  veiled?: boolean;
}) {
  return <div className="rounded-2xl border px-4 py-3.5 transition-[background-color,border-color,box-shadow,opacity] duration-300 ease-out" style={{
    background: on ? INK : '#F4F0E9',
    borderColor: on ? INK : BORDER,
    opacity: veiled ? 0.32 : 1,
    boxShadow: [on ? CORE_SHADOW : '', lit ? '0 0 0 3px rgba(234,88,12,0.2)' : ''].filter(Boolean).join(', ') || undefined
  }} data-id="element-549" data-mp="22yfhdqwxgft3">
      <p className="font-title text-[17px] font-bold leading-none tracking-[-0.015em]" style={{
      color: on ? '#FFFFFF' : FADE
    }} data-id="element-550" data-mp="3w2xn31doza9y">
        {title}
      </p>
      <p className="mt-1.5 text-[12px] font-medium leading-[1.35]" style={{
      color: on ? 'rgba(255,255,255,0.62)' : FADE
    }} data-id="element-551" data-mp="sk4ez43ondn6">
        {sub}
      </p>
    </div>;
}
function MobileReadout({
  isOn,
  modelMode,
  lit,
  spotlight
}: {
  isOn: (id: string) => boolean;
  modelMode: 'giga' | 'byo';
  lit: (id: string) => boolean;
  spotlight: boolean;
}) {
  const row = (id: NodeId) => <ReadoutRow key={id} id={id} on={isOn(id)} lit={lit(id)} veiled={spotlight && !lit(id)} data-id="element-552" data-mp="1bjs19g9xj7mj" />;
  return <div className="mt-8 space-y-7" data-id="element-553" data-mp="1iuwpeymnkgbm">
      <ReadoutGroup label="Your existing surfaces" data-id="element-554" data-mp="1ivyazx1aol7l">
        {(['gpt', 'claude', 'grok'] as NodeId[]).map(row)}
      </ReadoutGroup>

      <ReadoutGroup label="Giga surfaces" data-id="element-555" data-mp="1nvu7zcbkrcdn">
        {(['web', 'slack', 'teams'] as NodeId[]).map(row)}
      </ReadoutGroup>

      <div className="space-y-2" data-id="element-556" data-mp="1wu65lyauzafl">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#A0917F]" data-id="element-557" data-mp="1wvhak1g1io8a">
          What runs it
        </p>
        <CoreBlock title="Giga MCP Server" sub={nodeById('mcp').sub!} on={isOn('mcp')} lit={lit('mcp')} veiled={spotlight && !lit('mcp')} data-id="element-558" data-mp="21t90wr11e2ad" />
        <CoreBlock title="Giga Runtime" sub={nodeById('agent').sub!} on={isOn('agent')} lit={lit('agent')} veiled={spotlight && !lit('agent')} data-id="element-559" data-mp="2bjkgxbxzw1xm" />
        <CoreBlock title="AI model" sub={modelMode === 'giga' ? "Use Giga's default model" : 'Bring your own model'} on={isOn('agent')} lit={lit('model')} veiled={spotlight && !lit('model')} data-id="element-560" data-mp="2ghvp7i8n64dq" />
      </div>

      <ReadoutGroup label="Platform capabilities" data-id="element-561" data-mp="2pjmw2qex4h96">
        {(['apps', 'mem', 'skills', 'flows', 'pulses', 'tables', 'cloudb', 'fs'] as NodeId[]).map(row)}
      </ReadoutGroup>
    </div>;
}
export function ArchitectureWidget() {
  const [inactive, setInactive] = useState<NodeId[]>([]);
  const [modelMode, setModelMode] = useState<'giga' | 'byo'>('giga');
  /*
   * NO SPOTLIGHT HERE.
   *
   * A flash that dimmed everything except the blocks a tab had just changed was
   * tried and removed. The Tracks row in Company Brain can carry that treatment
   * because the thing being lit is a live event the reader had no part in; here
   * the reader pressed the tab, so darkening the drawing they just asked to see
   * fights them — and a diagram that changes weight for a beat and then changes
   * back is two transitions where the reader wanted zero. The drawing simply
   * settles into the new configuration.
   */
  const spotlight = false;
  const lit = (_id: string) => false;
  const veil = (_id: string) => 1;
  const isOn = (id: string) => !inactive.includes(id as NodeId);
  const groupOn = (gid: GroupId) => NODES.some(n => n.group === gid && isOn(n.id));
  const active = PRESETS.find(p => p.state.modelMode === modelMode && sameSet(p.state.inactive, inactive));
  const activePreset = active?.id ?? null;
  const endOn = (id: string) => GROUPS.some(g => g.id === id) ? groupOn(id as GroupId) : isOn(id);
  const modelOn = isOn('agent');
  return <div data-id="element-562" data-mp="1bopypxn5db0e">
      {/* The edges were 3px dashes marching on a 0.9s loop — the single most
       juvenile thing in the drawing, and perpetual motion nobody asked for. A
       fine dotted hairline that holds still reads as a schematic instead of a
       cartoon, and it stops competing with the blocks the reader is meant to be
       choosing between. */}
      <SectionIntro align="left" className="max-w-2xl" data-id="element-563" data-mp="16s918cu25kan">
        <h2 className="font-title text-4xl font-bold leading-[1.12] tracking-tight text-[#2B211A] md:text-[56px] md:leading-[1.14]" data-id="element-564" data-mp="2x3ncj8zyz8jc">
          Take what you need,
          <br data-id="element-565" data-mp="2cdjdconvhddc" />
          skip what you don't<span className="text-[#EA580C]" data-id="element-566" data-mp="2cci32meokuwo">.</span>
        </h2>
      </SectionIntro>

      {/* NOT A PILL. A recessed track with a raised marker is app chrome — it
       says "setting" when what these four actually are is chapters of one
       argument. Plain type on a hairline, with the active one underscored on
       the rule itself, is the same vocabulary as the page's chapter bar, and
       it lets the row anchor to the section's left edge instead of floating
       centred as an object of its own.
        Left-aligned, and the headline and claim with it: the row reads as a
       sequence you move along, which needs a starting edge. Centred, a
       four-item row has no obvious first item. */}
      <div className="mt-10 md:mt-14" data-id="element-567" data-mp="1er3w9nzcyhqb">
        {/* A SCROLLING RAIL ON PHONES. Wrapped, four labels this long stack
         four deep and the hairline they are meant to sit on ends up under a
         paragraph-shaped block of links — the row stops reading as a sequence
         you move along. Kept on one line it stays a row, and the cut-off
         fourth label is itself the affordance that there is more of it. */}
        <div role="tablist" aria-label="Configuration" className="flex items-end gap-x-7 overflow-x-auto border-b border-[#E7E0D4] pb-px [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:flex-wrap md:gap-x-10 md:gap-y-1 md:overflow-visible" data-id="element-568" data-mp="3lg4koop18les">
          {PRESETS.map(p => {
          const isActive = p.id === activePreset;
          return <button key={p.id} type="button" role="tab" aria-selected={isActive} onClick={() => {
            setInactive(p.state.inactive);
            setModelMode(p.state.modelMode);
          }} className={`relative -mb-px shrink-0 whitespace-nowrap pb-3.5 text-[14.5px] font-semibold tracking-[-0.005em] transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#EA580C]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${isActive ? 'text-[#2B211A]' : 'text-[#9A8A7A] hover:text-[#2B211A]'}`} data-id="element-569" data-mp="32nacqeptttk2">
                {p.label}
                {/* Sits ON the hairline rather than beside it, so the rule is
                 marked rather than doubled. */}
                <span aria-hidden="true" className={`absolute inset-x-0 -bottom-px h-[2px] rounded-full bg-[#EA580C] transition-opacity duration-200 ease-out ${isActive ? 'opacity-100' : 'opacity-0'}`} data-id="element-570" data-mp="2lmfskg2n95k1" />
              </button>;
        })}
        </div>

        {/* THE TAB'S CLAIM, not a caption. At 13.5px muted grey this was a
         footnote under a control, so the payoff of each configuration — the
         actual reason to pick it — was the quietest text on the section. In
         the display face it becomes the thing the tabs are answering.
         Fixed min-height so switching tabs never nudges the diagram. */}
        {/* ONE LINE from lg up. Capped at 26ch it broke every claim across two
         or three lines, so the slot had to reserve height for the longest of
         them and the shorter claims sat in leftover space. On one line the
         row of tabs and the line answering it read as a single horizontal
         beat, and the reserved height is a single line high. */}
        <p aria-live="polite" className="mt-7 flex min-h-[76px] max-w-[60ch] items-start font-title text-[26px] font-bold leading-[1.16] tracking-[-0.02em] text-[#2B211A] md:min-h-[44px] md:text-[30px] lg:max-w-none lg:whitespace-nowrap" data-id="element-571" data-mp="m42rmk351i2o">
          {active?.headline ?? 'Everything Giga does, alongside everything you already use.'}
        </p>
      </div>

      <div className="mt-1" data-id="element-572" data-mp="1ivejegp4p2a1">
        {/* Scaled to fit rather than scrolled. The diagram is one drawing —
         reading it through a 360px window means never seeing the relationship
         it exists to show, so it shrinks as a whole instead. The wrapper's
         height follows the same factor so no dead space is left below it. */}
        {/* Height trimmed to the drawing's actual extent (60 → 642) so the
         clearance above and below it matches. */}
        {/* PHONES GET A DIFFERENT DRAWING, NOT A SMALLER ONE.
         *
         * The schematic is 1160px wide and scales to fit, which on a 390px
         * phone is a factor of about 0.29 — every label lands near 4px and the
         * whole thing becomes a grey texture. Shrinking a diagram past
         * legibility is worse than replacing it: the relationship it exists to
         * show is gone AND the type is unreadable.
         *
         * So the same state is read out as a stack instead, in the order the
         * drawing flows: where you work, what runs it, what it can do. The
         * claim of the section is what is IN and what is OUT of a
         * configuration, and a list carries that on a phone better than a
         * schematic does — each skipped item says so in words. */}
        <div className="md:hidden" data-id="element-573" data-mp="2w7jt9g72pb0t">
          <MobileReadout isOn={isOn} modelMode={modelMode} lit={lit} spotlight={spotlight} data-id="element-574" data-mp="2s4vhazn2bnzm" />
        </div>

        <div className="hidden overflow-hidden md:block" style={{
        height: 'calc(700px * min(1, (100vw - 48px) / 1160))'
      }} data-id="element-575" data-mp="17n4i0tas2wel">
          <div className="relative mx-auto h-[700px] w-[1160px] origin-top-left" style={{
          transform: 'scale(min(1, calc((100vw - 48px) / 1160)))'
        }} data-id="element-576" data-mp="1boppfz4jmihe">
            {/* SOLID HAIRLINES, NO ARROWHEADS.
             *
             * Dotted strokes with filled triangles on the end are the visual
             * language of a slide diagram, and at 1.5px the dots also render
             * unevenly along a curve — the line looks frayed rather than fine.
             * A real schematic draws a continuous hairline and lets the blocks
             * it meets say what is connected to what; the arrowheads were
             * asserting a direction of flow the drawing never explains, and
             * they landed as small dark specks against the block edges. */}
            <svg width="1160" height="740" className="pointer-events-none absolute inset-0 transition-opacity duration-300 ease-out" style={{
            opacity: spotlight ? 0.3 : 1
          }} data-id="element-577" data-mp="9y16j5knf51n">
              {EDGES.map((e, i) => {
              const on = endOn(e.a) && endOn(e.b);
              const d = e.straight ? `M${e.x1} ${e.y1} L${e.x2} ${e.y2}` : `M${e.x1} ${e.y1} C${(e.x1 + e.x2) / 2} ${e.y1},${(e.x1 + e.x2) / 2} ${e.y2},${e.x2} ${e.y2}`;
              return <path key={i} d={d} fill="none" stroke={on ? '#CFC3B2' : '#EDE7DD'} strokeWidth={1} shapeRendering="geometricPrecision" data-id="element-578" data-mp="cilt7wptxrv1" data-mpi={i} />;
            })}
            </svg>

            {/* NOT CONTROLS. The drawing is now purely a readout of the tab
             above it: one control, one thing it changes. Individually
             clickable blocks meant two ways to drive the same state, and they
             could land the diagram in a configuration no tab described — so
             the reader could break the label off the picture. */}
            {GROUPS.map(g => {
            const on = groupOn(g.id);
            // A group panel stays lit if anything inside it just changed —
            // otherwise the container would recede out from under its own
            // spotlit blocks.
            const groupVeil = spotlight && !NODES.some(n => n.group === g.id && lit(n.id)) ? 0.32 : 1;
            return <React.Fragment key={g.id} data-id="element-579" data-mp="wkor3lfwngn1">
                  <div aria-hidden="true" className="absolute box-border rounded-[20px] transition-[background-color,border-color,opacity] duration-300 ease-out" style={{
                left: g.x,
                top: g.y,
                width: g.w,
                height: g.h,
                opacity: groupVeil,
                background: on ? '#FAF8F4' : '#F7F3EC',
                border: `1px solid ${on ? BORDER : '#EFE9DF'}`
              }} data-id="element-580" data-mp="wlzql92bi28c" />
                  {/* Was orange mono at 0.22em — three signals for a label. The
                   page's own eyebrow treatment in a muted brown reads as a
                   caption on a schematic rather than a heading shouting for
                   attention. */}
                  <div aria-hidden="true" className="absolute text-[11px] font-bold uppercase tracking-[0.14em] transition-[color,opacity] duration-300 ease-out" style={{
                left: g.x + 6,
                top: g.y - 24,
                opacity: groupVeil,
                color: on ? '#A0917F' : '#D5CBBE'
              }} data-id="element-581" data-mp="5dzj1hft04yo">
                    {g.label}
                  </div>
                </React.Fragment>;
          })}

            {/* AI model — its own node because the choice of model is a
             separate decision from the runtime that calls it. */}
            <div className="absolute box-border flex flex-col justify-center rounded-[20px] px-6 transition-[background-color,border-color,box-shadow,opacity] duration-300 ease-out" style={{
            left: 430,
            top: 465,
            width: 300,
            height: 96,
            opacity: veil('model'),
            background: modelOn ? INK : '#F4F0E9',
            border: `1px solid ${modelOn ? INK : BORDER}`,
            boxShadow: lit('model') ? `${modelOn ? CORE_SHADOW + ', ' : ''}0 0 0 3px rgba(234,88,12,0.2)` : modelOn ? CORE_SHADOW : 'none'
          }} data-id="element-582" data-mp="39spm1gfk7who">
              {/* NO SEGMENTED TOGGLE. A two-state pill sitting in a heading
               reads as a control, and this one has not been one since "BYO
               model" became a tab — so it was fake chrome. The value belongs
               in the description line, where it is simply stated. */}
              <span className="font-title text-[19px] font-bold leading-none tracking-[-0.015em] transition-colors duration-300" style={{
              color: modelOn ? '#FFFFFF' : FADE
            }} data-id="element-583" data-mp="2z59imq3tlta0">
                AI model
              </span>
              <span className="mt-2 text-[12px] font-medium leading-[1.35] transition-colors duration-300" style={{
              color: modelOn ? 'rgba(255,255,255,0.62)' : FADE
            }} data-id="element-584" data-mp="38y7331sdhsb9">
                {modelMode === 'giga' ? "Use Giga's default model" : 'Bring your own model'}
              </span>
            </div>

            {NODES.map(n => {
            const on = isOn(n.id);
            const core = n.id === 'agent' || n.id === 'mcp';
            const Icon = n.Icon;
            // THE FILL CARRIES THE CLASS, NOT THE SHAPE. The three core blocks
            // were capsules, which was wrong at this size: a pill radius on a
            // 74px box holding a left-aligned two-line text block leaves two
            // large dead lozenges and forces the copy into a narrow middle
            // channel. They now share the drawing's radius language and are
            // told apart by being solid ink with a cast — weight, not outline.
            const base = on ? core ? CORE_SHADOW : '0 1px 3px rgba(43,33,26,0.07)' : 'none';
            return <div key={n.id} aria-hidden="true" className={`absolute box-border flex select-none flex-col justify-center text-left transition-[background-color,border-color,color,box-shadow,opacity] duration-300 ease-out ${core ? 'rounded-[20px] px-6' : 'rounded-2xl px-[22px]'}`} style={{
              left: n.x,
              top: n.y,
              width: n.w,
              height: n.h,
              opacity: veil(n.id),
              background: on ? core ? INK : '#FFFFFF' : '#F4F0E9',
              border: `1px solid ${on ? core ? INK : BORDER : BORDER}`,
              color: on ? core ? '#FFFFFF' : INK : FADE,
              // The rim is the light itself: it marks a block that just entered
              // or left the configuration, whichever direction it moved.
              boxShadow: lit(n.id) ? `${base === 'none' ? '' : base + ', '}0 0 0 3px rgba(234,88,12,0.2)` : base
            }} data-id="element-585" data-mp="2il24udgyrl1u">
                  {/* Peripherals keep their mark — it says WHICH tool. The core
                   blocks carry no ornament at all: the orange bullet they used
                   to have sat in the pill's dead space labelling nothing, and
                   an unearned dot beside a heading is the cheapest tell there
                   is. Type alone, starting hard at the padding edge. */}
                  <span className={`flex items-center ${core ? '' : 'gap-3'}`} data-id="element-586" data-mp="1wk8q2680j5bb">
                    {!core && (Icon ? <span className="shrink-0 transition-opacity duration-300" style={{
                  opacity: on ? 1 : 0.4
                }} data-id="element-587" data-mp="1itbymiptou5u">
                        <Icon size={20} strokeWidth={1.9} color={on ? ORANGE : '#BFB3A4'} data-id="element-588" data-mp="2bgpzut0k0bvr" />
                      </span> : n.logo ? <img src={n.logo} alt="" aria-hidden="true" className="size-[22px] shrink-0 object-contain transition-[filter] duration-300" style={{
                  filter: on ? 'none' : 'grayscale(1) opacity(0.4)'
                }} data-id="element-589" data-mp="2fi1i2dh3lutx" /> : <span className="size-[9px] shrink-0 rounded-full transition-colors duration-300" style={{
                  background: on ? ORANGE : '#D6CCC0',
                  boxShadow: on ? '0 0 0 3px rgba(234,88,12,0.16)' : 'none'
                }} data-id="element-590" data-mp="26gtsqkwk84bz" />)}
                    <span className="flex min-w-0 flex-col" data-id="element-591" data-mp="nix886zn3x4w">
                      <span className={core ? 'font-title text-[19px] font-bold leading-none tracking-[-0.015em]' : 'text-[16.5px] font-semibold'} data-id="element-592" data-mp="nhdw8sl0o88x">
                        {n.label}
                      </span>
                      {n.sub && <span className={`font-medium leading-[1.35] transition-colors duration-300 ${core ? 'mt-2 text-[12px]' : 'mt-0.5 text-[11.5px]'}`} style={{
                    color: on ? core ? 'rgba(255,255,255,0.62)' : '#8A7A6A' : FADE
                  }} data-id="element-593" data-mp="wh99r421ofc4">
                          {n.sub}
                        </span>}
                    </span>
                  </span>
                </div>;
          })}
          </div>
        </div>
      </div>

    </div>;
}